import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'

export type ProviderName =
  | 'gemini'
  | 'claude'
  | 'openai'
  | 'perplexity'
  | 'openrouter'

/**
 * Model catalogue entry — shape matches MODEL_CATALOGUE in @intelhub/ai. We
 * intentionally keep the interface lean here; richer typing can live in
 * `@intelhub/ai` and be re-exported if callers need it.
 */
export interface CatalogueModel {
  id: string
  label?: string
  [key: string]: unknown
}

/**
 * GET /admin/providers/models returns a keyed-by-provider map. When the
 * `provider` query param is set, only that provider's entry is populated;
 * otherwise all providers are returned. The OpenRouter entry may be the
 * live API result (when a key is present) or the static fallback catalogue.
 */
export interface ProviderModelsResponse {
  catalogue: Partial<Record<ProviderName, CatalogueModel[] | CatalogueModel>>
}

/**
 * Fetch the model catalogue for one provider or all providers.
 * Pass `provider` to scope the response; leave undefined for the full set.
 */
export function useAIProviderModels(provider?: ProviderName) {
  return useQuery({
    queryKey: ['ai-provider-models', provider ?? 'all'],
    queryFn: () => {
      const qs = provider ? `?provider=${encodeURIComponent(provider)}` : ''
      return api.get<ProviderModelsResponse>(`/admin/providers/models${qs}`)
    },
    staleTime: 24 * 60 * 60 * 1000,
  })
}
